import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Plus, Trash2, RefreshCw, Megaphone, Star, Tag } from 'lucide-react';
import { useAdminLang } from '../AdminLangContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface AdminContext {
  secret: string;
}

const API_BASE = import.meta.env.VITE_API_URL || '/api';

/* ------------------------------------------------------------------ */
/*  Types                                                               */
/* ------------------------------------------------------------------ */

interface Campaign {
  id: string;
  name: string;
  type: 'banner' | 'flash_sale' | 'boost' | 'popup';
  isActive: boolean;
  startDate?: string;
  endDate?: string;
  message?: string;
  imageUrl?: string;
  targetUrl?: string;
  createdAt?: string;
}

interface PromotedProduct {
  id: string;
  name?: string;
  imageUrl?: string;
  price?: number;
}

interface PromotedBrand {
  brand: string;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                             */
/* ------------------------------------------------------------------ */

function formatDate(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('el-GR');
}

async function apiFetch<T>(path: string, secret: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'x-admin-secret': secret,
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }
  // Handle 204 No Content
  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
}

/* ------------------------------------------------------------------ */
/*  Banners Tab                                                         */
/* ------------------------------------------------------------------ */

const CAMPAIGN_TYPE_LABELS: Record<Campaign['type'], string> = {
  banner: 'Banner',
  flash_sale: 'Flash Sale',
  boost: 'Boost',
  popup: 'Popup',
};

const EMPTY_CAMPAIGN = { name: '', type: 'banner' as Campaign['type'], message: '', targetUrl: '', imageUrl: '' };

function BannersTab({ secret }: { secret: string }) {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_CAMPAIGN);
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ campaigns: Campaign[] }>('/admin/marketing/campaigns', secret);
      setCampaigns(data.campaigns ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [secret]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this campaign?')) return;
    setDeletingId(id);
    try {
      await apiFetch<void>(`/admin/marketing/campaigns/${id}`, secret, { method: 'DELETE' });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCreate = async () => {
    if (!form.name.trim()) { setFormError('Name is required'); return; }
    setCreating(true);
    setFormError(null);
    try {
      await apiFetch<void>('/admin/marketing/campaigns', secret, {
        method: 'POST',
        body: JSON.stringify({
          name: form.name.trim(),
          type: form.type,
          message: form.message.trim() || undefined,
          targetUrl: form.targetUrl.trim() || undefined,
          imageUrl: form.imageUrl.trim() || undefined,
          isActive: true,
        }),
      });
      setDialogOpen(false);
      setForm(EMPTY_CAMPAIGN);
      await load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Megaphone className="h-5 w-5" />
              Campaigns &amp; Banners
            </CardTitle>
            <CardDescription>Manage promotional banners and campaign messages</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button size="sm" onClick={() => setDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Campaign
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {error && (
          <div className="mx-6 mb-4 rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}
        {loading ? (
          <div className="py-16 text-center text-muted-foreground">Loading...</div>
        ) : campaigns.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground">No campaigns yet.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Start</TableHead>
                <TableHead>End</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{CAMPAIGN_TYPE_LABELS[c.type] ?? c.type}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={c.isActive ? 'default' : 'secondary'}>
                      {c.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{formatDate(c.startDate)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{formatDate(c.endDate)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Delete"
                      disabled={deletingId === c.id}
                      onClick={() => handleDelete(c.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New Campaign</DialogTitle>
            <DialogDescription>Create a new banner or promotional campaign.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="camp-name">Name</Label>
              <Input
                id="camp-name"
                placeholder="Summer Sale Banner"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="camp-type">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v as Campaign['type'] }))}>
                <SelectTrigger id="camp-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(CAMPAIGN_TYPE_LABELS) as Campaign['type'][]).map((t) => (
                    <SelectItem key={t} value={t}>{CAMPAIGN_TYPE_LABELS[t]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="camp-message">Message (optional)</Label>
              <Input
                id="camp-message"
                placeholder="Up to 50% off selected wines!"
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="camp-url">Target URL (optional)</Label>
              <Input
                id="camp-url"
                placeholder="https://botilia.gr/prosfores"
                value={form.targetUrl}
                onChange={(e) => setForm((f) => ({ ...f, targetUrl: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="camp-image">Image URL (optional)</Label>
              <Input
                id="camp-image"
                placeholder="https://cdn.botilia.gr/banner.jpg"
                value={form.imageUrl}
                onChange={(e) => setForm((f) => ({ ...f, imageUrl: e.target.value }))}
              />
            </div>
            {formError && <p className="text-sm text-destructive">{formError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialogOpen(false); setForm(EMPTY_CAMPAIGN); setFormError(null); }}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/*  Promoted Products Tab                                               */
/* ------------------------------------------------------------------ */

function PromotedProductsTab({ secret, lang }: { secret: string; lang: string }) {
  const [products, setProducts] = useState<PromotedProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newIds, setNewIds] = useState('');
  const [adding, setAdding] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const langQs = `?lang=${encodeURIComponent(lang)}`;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ products: PromotedProduct[] }>(`/admin/promoted-products${langQs}`, secret);
      setProducts(data.products ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [secret, langQs]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    const ids = newIds.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
    if (ids.length === 0) { setFormError('Enter at least one product ID'); return; }
    setAdding(true);
    setFormError(null);
    try {
      await apiFetch<void>(`/admin/promoted-products${langQs}`, secret, {
        method: 'POST',
        body: JSON.stringify({ productIds: ids }),
      });
      setDialogOpen(false);
      setNewIds('');
      await load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to add');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (id: string) => {
    setRemovingId(id);
    try {
      await apiFetch<void>(`/admin/promoted-products/${id}${langQs}`, secret, { method: 'DELETE' });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Remove failed');
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Star className="h-5 w-5" />
              Promoted Products
            </CardTitle>
            <CardDescription>Products pinned to the top of search results</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button size="sm" onClick={() => setDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Products
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="mb-4 rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}
        {loading ? (
          <div className="py-16 text-center text-muted-foreground">Loading...</div>
        ) : products.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground">No promoted products yet.</div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((p) => (
              <div
                key={p.id}
                className="flex items-center gap-3 rounded-lg border bg-card p-3"
              >
                {p.imageUrl && (
                  <img src={p.imageUrl} alt={p.name} className="h-12 w-12 rounded object-cover" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{p.name ?? p.id}</p>
                  {p.price !== undefined && (
                    <p className="text-xs text-muted-foreground">{p.price.toFixed(2)} €</p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  title="Remove"
                  disabled={removingId === p.id}
                  onClick={() => handleRemove(p.id)}
                  className="text-destructive hover:text-destructive flex-shrink-0"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Promoted Products</DialogTitle>
            <DialogDescription>Enter product IDs (one per line or comma-separated).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="prod-ids">Product IDs</Label>
              <textarea
                id="prod-ids"
                className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder={"prod-123\nprod-456\nprod-789"}
                value={newIds}
                onChange={(e) => setNewIds(e.target.value)}
              />
            </div>
            {formError && <p className="text-sm text-destructive">{formError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialogOpen(false); setNewIds(''); setFormError(null); }}>
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={adding}>
              {adding ? 'Adding...' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/*  Promoted Brands Tab                                                 */
/* ------------------------------------------------------------------ */

function PromotedBrandsTab({ secret, lang }: { secret: string; lang: string }) {
  const [brands, setBrands] = useState<PromotedBrand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newBrands, setNewBrands] = useState('');
  const [adding, setAdding] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [removingBrand, setRemovingBrand] = useState<string | null>(null);

  const langQs = `?lang=${encodeURIComponent(lang)}`;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ brands: PromotedBrand[] }>(`/admin/promoted-brands${langQs}`, secret);
      setBrands(data.brands ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [secret, langQs]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    const list = newBrands.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
    if (list.length === 0) { setFormError('Enter at least one brand name'); return; }
    setAdding(true);
    setFormError(null);
    try {
      await apiFetch<void>(`/admin/promoted-brands${langQs}`, secret, {
        method: 'POST',
        body: JSON.stringify({ brands: list }),
      });
      setDialogOpen(false);
      setNewBrands('');
      await load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to add');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (brand: string) => {
    setRemovingBrand(brand);
    try {
      await apiFetch<void>(`/admin/promoted-brands/${encodeURIComponent(brand)}${langQs}`, secret, { method: 'DELETE' });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Remove failed');
    } finally {
      setRemovingBrand(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              Promoted Brands
            </CardTitle>
            <CardDescription>Brands promoted at the top of brand filters</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button size="sm" onClick={() => setDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Brands
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="mb-4 rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}
        {loading ? (
          <div className="py-16 text-center text-muted-foreground">Loading...</div>
        ) : brands.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground">No promoted brands yet.</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {brands.map((b) => (
              <div
                key={b.brand}
                className="flex items-center gap-1.5 rounded-full border bg-card px-3 py-1.5"
              >
                <span className="text-sm font-medium">{b.brand}</span>
                <button
                  type="button"
                  onClick={() => handleRemove(b.brand)}
                  disabled={removingBrand === b.brand}
                  className="ml-1 rounded-full p-0.5 text-muted-foreground transition-colors hover:text-destructive disabled:opacity-50"
                  title="Remove"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Promoted Brands</DialogTitle>
            <DialogDescription>Enter brand names (one per line or comma-separated).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="brand-names">Brand Names</Label>
              <textarea
                id="brand-names"
                className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder={"Domaine Gerovassiliou\nAlpha Estate\nKir-Yianni"}
                value={newBrands}
                onChange={(e) => setNewBrands(e.target.value)}
              />
            </div>
            {formError && <p className="text-sm text-destructive">{formError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialogOpen(false); setNewBrands(''); setFormError(null); }}>
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={adding}>
              {adding ? 'Adding...' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                                */
/* ------------------------------------------------------------------ */

export default function AdminMarketingPage() {
  const { secret } = useOutletContext<AdminContext>();
  const { lang } = useAdminLang();
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Marketing Controls</h1>
        <p className="text-muted-foreground">Manage banners, promoted products and brands</p>
      </div>
      <Tabs defaultValue="banners">
        <TabsList>
          <TabsTrigger value="banners">Banners</TabsTrigger>
          <TabsTrigger value="products">Promoted Wines</TabsTrigger>
          <TabsTrigger value="brands">Promoted Brands</TabsTrigger>
        </TabsList>
        <TabsContent value="banners" className="mt-4">
          <BannersTab secret={secret} />
        </TabsContent>
        <TabsContent value="products" className="mt-4">
          <PromotedProductsTab secret={secret} lang={lang} />
        </TabsContent>
        <TabsContent value="brands" className="mt-4">
          <PromotedBrandsTab secret={secret} lang={lang} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
